import { type FC, useState } from 'react';
import { PiArrowsDownUp } from 'react-icons/pi';
import Button from '../../../atoms/buttons/button';
import {
  type Sort,
  type SortFieldOptions,
} from 'src/modules/shared/types/filters.types';
import { Text } from '../../../atoms/text';
import { CountBadge } from '../../../atoms/count-badge';
import { SortBuilder } from './sort-builder';
import { getTextSize } from 'src/modules/shared/components/atoms/text/text.styles';
import Modal from '../../modals/modal';
import { ModalFooter } from '../../modals/modal-footer';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

export interface AdvancedSortsProps {
  fieldsOptions: SortFieldOptions[];
  onChange: (sorts: Sort[]) => void;
  activeSortCount: number;
  sorts: Sort[];
}

export const AdvancedSorts: FC<AdvancedSortsProps> = ({
  fieldsOptions,
  onChange,
  activeSortCount = 0,
  sorts: initialSorts,
}) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [localSorts, setLocalSorts] = useState<Sort[]>(initialSorts);

  const handleOpen = () => {
    setLocalSorts(initialSorts);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setLocalSorts(initialSorts);
  };

  const handleApply = () => {
    onChange(localSorts);
    setIsOpen(false);
  };

  const sortButtonClasses = `flex items-center gap-2 px-3 ${activeSortCount > 0 ? 'bg-primary-50' : ''}`;
  const sortButton = (
    <Button
      appearance="ghost"
      size="md"
      className={sortButtonClasses}
      onClick={handleOpen}
    >
      <div className="flex items-center gap-2">
        <PiArrowsDownUp className="w-5 h-5" />
        <Text size={getTextSize('md')} className="hidden 2xl:inline">
          {t('shared.advancedSorts.button')}
        </Text>
        {activeSortCount > 0 && (
          <CountBadge count={activeSortCount} size="sm" variant="primary" />
        )}
      </div>
    </Button>
  );

  return (
    <>
      {sortButton}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        header={
          <div className="space-y-1">
            <Text
              as="h3"
              size="h6"
              weight="semibold"
              className="text-primary-900"
            >
              {t('shared.advancedSorts.title')}
            </Text>
            <Text size="small" className="text-neutral-600">
              {t('shared.advancedSorts.description')}
            </Text>
          </div>
        }
        body={
          <div className="min-h-0 w-full overflow-y-auto bg-neutral-50 p-4 sm:p-5">
            <SortBuilder
              fieldsOptions={fieldsOptions}
              sorts={localSorts}
              onChange={setLocalSorts}
            />
          </div>
        }
        footer={
          <ModalFooter
            onCancel={handleClose}
            onConfirm={handleApply}
            cancelText={t('shared.actions.cancel')}
            confirmText={t('shared.actions.apply')}
          />
        }
      />
    </>
  );
};
